import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { auth } from '../firebase';
import { useAuth } from './AuthContext';
import { schedulerApiFetch } from '../api/schedulerClient';
import PublishPasswordModal from '../components/PublishPasswordModal';

const PublishAccessContext = createContext(null);

const MAX_PASSWORD_LENGTH = 128;
const MAX_FAILED_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;

function isTypingTarget(target) {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

export function PublishAccessProvider({ children }) {
  const { user } = useAuth();
  const [adminModeUnlocked, setAdminModeUnlocked] = useState(false);
  const [publishSelectionUnlocked, setPublishSelectionUnlocked] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordModalBlocked, setPasswordModalBlocked] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  const verifyAttemptRef = useRef({ failures: 0, lockedUntil: 0 });
  const verifyInFlightRef = useRef(false);

  useEffect(() => {
    if (!user) {
      setAdminModeUnlocked(false);
      setPublishSelectionUnlocked(false);
      setPasswordModalOpen(false);
      setPasswordModalBlocked(false);
      setPasswordError(null);
    }
  }, [user]);

  const closePasswordModal = useCallback(() => {
    if (passwordVerifying || verifyInFlightRef.current) return;
    setPasswordModalOpen(false);
    setPasswordModalBlocked(false);
    setPasswordError(null);
  }, [passwordVerifying]);

  const openUnlockModal = useCallback(() => {
    if (passwordModalOpen || verifyInFlightRef.current) return;

    if (!auth.currentUser) {
      setPasswordModalBlocked(true);
      setPasswordError('Sign in before unlocking admin mode.');
      setPasswordModalOpen(true);
      return;
    }

    setPasswordModalBlocked(false);
    setPasswordError(null);
    setPasswordModalOpen(true);
  }, [passwordModalOpen]);

  useEffect(() => {
    function onKeyDown(e) {
      if (!e.shiftKey || (e.key !== 'V' && e.key !== 'v')) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();

      if (adminModeUnlocked) {
        setAdminModeUnlocked(false);
        setPublishSelectionUnlocked(false);
        closePasswordModal();
        return;
      }

      openUnlockModal();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [adminModeUnlocked, closePasswordModal, openUnlockModal]);

  useEffect(() => {
    function onKeyDown(e) {
      if (!e.shiftKey || (e.key !== 'X' && e.key !== 'x')) return;
      if (!adminModeUnlocked) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      setPublishSelectionUnlocked((prev) => !prev);
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [adminModeUnlocked]);

  const verifyPasswordAndProceed = useCallback(async (password) => {
    if (verifyInFlightRef.current) return;

    const now = Date.now();
    if (now < verifyAttemptRef.current.lockedUntil) {
      const secondsLeft = Math.ceil((verifyAttemptRef.current.lockedUntil - now) / 1000);
      setPasswordError(`Too many failed attempts. Try again in ${secondsLeft}s.`);
      return;
    }

    if (typeof password !== 'string' || !password.trim()) {
      setPasswordError('Password is required.');
      return;
    }

    if (password.length > MAX_PASSWORD_LENGTH) {
      setPasswordError('Password is too long.');
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setPasswordError('Sign in before unlocking admin mode.');
      return;
    }

    verifyInFlightRef.current = true;
    setPasswordVerifying(true);
    setPasswordError(null);

    try {
      const token = await currentUser.getIdToken();
      if (!token) {
        setPasswordError('Session expired. Sign in again and retry.');
        return;
      }

      const response = await schedulerApiFetch('admin-access/verify', {
        method: 'POST',
        bearerToken: token,
        body: { password },
      });

      if (!response.ok) {
        verifyAttemptRef.current.failures += 1;
        if (verifyAttemptRef.current.failures >= MAX_FAILED_ATTEMPTS) {
          verifyAttemptRef.current.lockedUntil = Date.now() + LOCKOUT_MS;
          verifyAttemptRef.current.failures = 0;
          setPasswordError('Too many failed attempts. Wait 1 minute before trying again.');
        } else if (response.status === 403) {
          setPasswordError('Incorrect admin password. Try again.');
        } else if (response.status === 401) {
          setPasswordError('Session expired. Sign in again and retry.');
        } else {
          const data = await response.json().catch(() => ({}));
          const detail = typeof data.detail === 'string' ? data.detail : 'Password verification failed.';
          setPasswordError(detail);
        }
        return;
      }

      verifyAttemptRef.current.failures = 0;
      verifyAttemptRef.current.lockedUntil = 0;
      setAdminModeUnlocked(true);
      setPasswordModalOpen(false);
      setPasswordModalBlocked(false);
      setPasswordError(null);
    } catch {
      setPasswordError('Password verification failed. Try again.');
    } finally {
      verifyInFlightRef.current = false;
      setPasswordVerifying(false);
    }
  }, []);

  return (
    <PublishAccessContext.Provider value={{ adminModeUnlocked, publishSelectionUnlocked }}>
      {children}
      {passwordModalOpen && (
        <PublishPasswordModal
          error={passwordError}
          verifying={passwordVerifying}
          blocked={passwordModalBlocked}
          onCancel={closePasswordModal}
          onSubmit={verifyPasswordAndProceed}
        />
      )}
    </PublishAccessContext.Provider>
  );
}

export function usePublishAccess() {
  const context = useContext(PublishAccessContext);
  if (!context) {
    throw new Error('usePublishAccess must be used within a PublishAccessProvider');
  }
  return context;
}
